import React, { useContext } from "react";
import {
    Grid,
    GridRow,
    GridColumn,
    SearchDropdown,
    Stack,
    ClearFiltersButton,
    MultiselectSearchDropdown,
    ListTable,
    mapToDropdownOption,
    DropdownMultiselect,
    DatePicker,
    ContentSection,
    ButtonRenderProps
} from "sprint-common-web";

import { CloudAssetOrigin } from "../../../common/types/AssetOrigin";
import { AssetStatus } from "../../../common/types/AssetStatus";
import { AssetType } from "../../../common/types/AssetType";
import { RecordType } from "../../../common/types/RecordType";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { getEpisodeText } from "../../../common/utils/getEpisodeText";
import { mapEnumToDropdownOptions } from "../../../common/utils/mapEnumToDropdownOptions";
import { AssetsWarning } from "../common/AssetsWarning";
import {
    EditAssetListingColumnsConfigButton,
    EditAssetListingColumnsConfigButtonProps
} from "../common/config/EditFileRecordListingColumnsConfigButton";
import { createAssetListTableColumns } from "../common/createAssetListTableColumns";
import { renderAssetRow } from "../common/renderAssetRow";
import { SingleTvSeriesContext } from "./context/SingleTvSeriesContext";
import { useTvSeriesAssets } from "./hooks/useTvSeriesAssets";
import { useTvSeriesAssetsListFilters } from "./hooks/useTvSeriesAssetsListFilters";
import { TvSeriesAssetsListBreadcrumbs } from "./TvSeriesAssetsListBreadcrumbs";

export const TvSeriesAssetsList: React.FunctionComponent = () => {
    const { tvSeries, isLoading: isContextLoading, listingConfig, saveListingConfig } = useContext(
        SingleTvSeriesContext
    );

    const {
        filters,
        seasons,
        episodes,
        versionDetails,
        trailersOptions,
        setTrailers,
        setAssetTypes,
        setAssetOrigins,
        setAssetRecordTypes,
        setSeason,
        setEpisode,
        setVersionsTypes,
        setStatuses,
        setPage,
        setPageSize,
        setSort,
        setDistributors,
        setCreatedDateFrom,
        setCreatedDateTo,
        setUpdatedDateFrom,
        setUpdatedDateTo,
        clearFilters
    } = useTvSeriesAssetsListFilters();

    const { items, totalItems, isLoading } = useTvSeriesAssets({ filters });

    const columns = createAssetListTableColumns({ config: listingConfig });

    const configButton: ButtonRenderProps<EditAssetListingColumnsConfigButtonProps> = {
        render: EditAssetListingColumnsConfigButton,
        listingConfig,
        saveListingConfig,
        pageSize: filters.pageSize,
        setPageSize: setPageSize
    };

    return (
        <>
            <PageHeader>
                <TvSeriesAssetsListBreadcrumbs tvSeriesName={tvSeries?.title} isLoading={isContextLoading} />
            </PageHeader>
            <AssetsWarning items={items} />
            <ContentSection header="Tv Series assets" buttons={[configButton]}>
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <SearchDropdown
                                placeholder="Season"
                                options={seasons.map((season) => mapToDropdownOption(season))}
                                selectedKey={filters.season}
                                onChange={setSeason}
                                underlined={true}
                                disableSort={true}
                                disabled={!seasons.length}
                                tooltip={!seasons.length ? "No seasons for current combination of filters" : undefined}
                            />
                        </GridColumn>
                        <GridColumn sm={6}>
                            <SearchDropdown
                                placeholder="Episode"
                                options={episodes.map((episode) => mapToDropdownOption(episode, getEpisodeText))}
                                selectedKey={filters.episode}
                                onChange={setEpisode}
                                underlined={true}
                                disabled={!episodes.length}
                                tooltip={
                                    !episodes.length ? "No episodes for current combination of filters" : undefined
                                }
                            />
                        </GridColumn>
                        <GridColumn sm={4}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton isLabeled={true} onClear={clearFilters} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                placeholder="Distributors"
                                options={versionDetails.distributors.map((distributor) =>
                                    mapToDropdownOption(distributor)
                                )}
                                selectedKeys={filters.distributors}
                                onChange={setDistributors}
                                disabled={!versionDetails.distributors.length}
                                tooltip={
                                    !versionDetails.distributors.length
                                        ? "No distributors for current combination of filters"
                                        : undefined
                                }
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                placeholder="Version types"
                                options={versionDetails.versionTypes.map((type) => mapToDropdownOption(type))}
                                selectedKeys={filters.versionTypes}
                                onChange={setVersionsTypes}
                                disabled={!versionDetails.versionTypes.length}
                                tooltip={
                                    !versionDetails.versionTypes.length
                                        ? "No version types for current combination of filters"
                                        : undefined
                                }
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Statuses"
                                options={mapEnumToDropdownOptions(AssetStatus)}
                                selectedKeys={filters.statuses?.map((status) => status.toString())}
                                onChange={setStatuses}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset type"
                                selectedKeys={filters.types?.map((t) => t.toString())}
                                onChange={setAssetTypes}
                                options={mapEnumToDropdownOptions(AssetType)}
                                testId="tvseries-asset-types-filter"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset record type"
                                selectedKeys={filters.recordTypes?.map((t) => t.toString())}
                                onChange={setAssetRecordTypes}
                                options={mapEnumToDropdownOptions(RecordType)}
                                testId="tvseries-asset-record-types-filter"
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset origin"
                                selectedKeys={filters.origins?.map((t) => t.toString())}
                                onChange={setAssetOrigins}
                                options={mapEnumToDropdownOptions(CloudAssetOrigin)}
                                testId="tvseries-asset-origins-filter"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Trailers"
                                selectedKeys={filters.trailers}
                                onChange={setTrailers}
                                options={trailersOptions}
                                testId="assets-filters-trailers"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-created-date-from"
                                placeholder="Created date from"
                                value={filters.createdDateFrom}
                                onDateSelect={setCreatedDateFrom}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-created-date-to"
                                placeholder="Created date to"
                                value={filters.createdDateTo}
                                onDateSelect={setCreatedDateTo}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-updated-date-from"
                                placeholder="Updated date from"
                                value={filters.updatedDateFrom}
                                onDateSelect={setUpdatedDateFrom}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-updated-date-to"
                                placeholder="Updated date to"
                                value={filters.updatedDateTo}
                                onDateSelect={setUpdatedDateTo}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn>
                            <ListTable
                                columns={columns}
                                items={items}
                                layoutMode="fixedColumns"
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                onRenderRow={renderAssetRow}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                isLoading={isContextLoading || isLoading}
                                onSortChange={setSort}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};
